import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Tooltip from '@mui/material/Tooltip'

import BallotRoundedIcon from '@mui/icons-material/BallotRounded'

import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'

export type DoctorSelectionDialogProps = {
  doctors: Array<{
    id: string
    name: string
  }>
  onSelect?: (doctorId: string) => void
  selectedDoctor?: string
  title: string
}

export function DoctorSelectionDialog(props: DoctorSelectionDialogProps) {
  const { doctors, onSelect, selectedDoctor: selectedDoctorProp, title } = props
  const { t } = useTranslation()

  const [open, setOpen] = useState<boolean>(false)
  const [selectedDoctor, setSelectedDoctor] = useState<string>()

  const options = useMemo(() => doctors.map((doctor) => ({ label: doctor.name, value: doctor.id })), [doctors])

  useEffect(() => {
    if (open) setSelectedDoctor(selectedDoctorProp)
  }, [open, selectedDoctorProp])

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedDoctor(value)
  }

  const handleSelect = () => {
    if (selectedDoctor) onSelect?.(selectedDoctor)
  }

  const toggleDialog = () => {
    setOpen(!open)
  }

  return (
    <Box>
      <Tooltip title={title}>
        <span>
          <IconButton disabled={!doctors.length} onClick={toggleDialog}>
            <Badge color="info" badgeContent={doctors.length} max={9} showZero>
              <BallotRoundedIcon htmlColor="#FFF" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={toggleDialog} scroll="paper">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <RadioGroupInput onChange={handleChange} options={options} value={selectedDoctor} />
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleDialog} variant="neutral">
            {t('common:button.close.label')}
          </Button>
          <Button onClick={handleSelect} variant="contained">
            {t('common:button.select.label')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DoctorSelectionDialog
